import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';
import { useAuth } from '../hooks/useAuth';
import '../styles/send.css';
import { PublicKey, Transaction } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { getAssociatedTokenAddress, createTransferInstruction } from '@solana/spl-token';
import bs58 from 'bs58';
import RateConfirmDialog from './RateConfirmDialog';
import { useUser } from '@civic/auth-web3/react';
import nacl from 'tweetnacl'; // For optional signature verification
import { Buffer } from 'buffer';
import { v4 as uuidv4 } from 'uuid';
import { CONFIG } from '../config';
import { deviceFingerprint } from '../utils/deviceFingerprint';
import debugEnv from '../utils/envDebug';
import { logger } from '../utils/logger';

// Custom logger that only logs in development
const customLogger = {
  log: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(...args);
    }
  },
  error: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(...args);
    } else {
      // In production, still log errors but without sensitive information
      console.error('Error occurred in SendBank component');
    }
  }
};

const BASE_URL = CONFIG.PAJ_API_URL;

// Common headers for API requests
const commonHeaders = {
  'Content-Type': 'application/json'
};

// Debug environment variables
debugEnv();

// Get API key from config
const API_KEY = CONFIG.PAJ_BUSINESS_API_KEY;

// Verify API key is available
if (!API_KEY) {
  customLogger.error('PAJ Business API key is not configured');
} else {
  customLogger.log('API Key loaded successfully from config');
}

// Headers specifically for the Initiate Session endpoint
const initiateHeaders = {
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
  'Accept': 'application/json'
};

// Solana setup
const connection = rpc;
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');
const FEE_WALLET = new PublicKey('GjG2o2KXikkfEDBz1a8NAtgXrzXuYvK5GAMxnGXvFHzU');
const WITHDRAWAL_FEE_PERCENT = 0.5; // 0.5% fee
const MIN_WITHDRAWAL_AMOUNT = 0.5; // 0.5 USDC minimum

// TTL (1 hour) storage helpers
const TTL_MS = 3600000;

// Helper to log PAJ API interactions to our database
const logPajInteraction = async (endpoint, requestData, responseData = null, error = null) => {
  try {
    const logData = {
      endpoint,
      request: requestData,
      timestamp: new Date().toISOString()
    };
    
    if (responseData) logData.response = responseData;
    if (error) logData.error = error.message || String(error);
    
    // Log to our database
    await fetch('/api/paj/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData)
    });
  } catch (logError) {
    customLogger.error('Failed to log PAJ interaction:', logError);
    // Don't throw, we don't want to break the main flow
  }
};
function setWithExpiry(key, value) {
  const item = { value, expiry: Date.now() + TTL_MS };
  localStorage.setItem(key, JSON.stringify(item));
}
function getWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  try {
    const item = JSON.parse(itemStr);
    if (Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

// Optional: Helper to verify signature (for debugging, not required in production)
function verifySignature(payload, signatureBase58, publicKeyBase58) {
  const messageBytes = new TextEncoder().encode(JSON.stringify(payload));
  const signature = bs58.decode(signatureBase58);
  const publicKeyBytes = (new PublicKey(publicKeyBase58)).toBytes();
  return nacl.sign.detached.verify(messageBytes, signature, publicKeyBytes);
}

function SendBank() {
  const navigate = useNavigate();
  const { wallet, balances, isLoading: walletLoading, error: walletError } = useWallet();
  const { email } = useAuth();
  const userContext = useUser();
  const civicWallet = userContext?.solana?.wallet;
  const [amount, setAmount] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [banksList, setBanksList] = useState([]);
  const [newBankId, setNewBankId] = useState('');
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedName, setResolvedName] = useState('');
  const [resolveError, setResolveError] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false); // Added showRateDialog state

  // Fetch user bank accounts from PAJ API
  const fetchUserBankAccounts = async () => {
    try {
      if (!email) {
        customLogger.log('No email provided, skipping bank account fetch');
        return;
      }
      
      customLogger.log('Initiating session with email:', email);
      customLogger.log('Using API key:', import.meta.env.VITE_PAJ_BUSINESS_API_KEY ? 'Present' : 'Missing');
      
      // First, initiate the session
      const initRes = await fetch(`${BASE_URL}/pub/initiate`, { 
        method: 'POST', 
        headers: {
          ...initiateHeaders,
          'Accept': 'application/json'
        }, 
        body: JSON.stringify({ 
          email: email.trim() 
        }) 
      });
      
      const initResponseData = await initRes.json().catch(() => ({}));
      customLogger.log('Initiate response status:', initRes.status);
      customLogger.log('Initiate response data:', initResponseData);
      
      if (!initRes.ok) {
        throw new Error(initResponseData.message || 'Session initiation failed');
      }
      
      // Generate device signature and ensure it's a string
      let deviceSignature = await deviceFingerprint.get();
      deviceSignature = String(deviceSignature);
      
      // Verify the session with OTP
      const verifyPayload = { 
        email: email.trim(), 
        otp: '1234',
        deviceSignature
      };
      
      customLogger.log('Verifying session with payload:', {
        email: verifyPayload.email,
        otp: verifyPayload.otp,
        deviceSignature: '***' + (deviceSignature ? deviceSignature.slice(-4) : 'null')
      });
      
      const verifyRes = await fetch(`${BASE_URL}/pub/verify`, { 
        method: 'POST', 
        headers: {
          ...commonHeaders,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }, 
        body: JSON.stringify(verifyPayload),
        mode: 'cors'
      });
      
      const verifyResponseText = await verifyRes.text();
      let verifyResponseData;
      
      try {
        verifyResponseData = verifyResponseText ? JSON.parse(verifyResponseText) : {};
      } catch (e) {
        customLogger.error('Failed to parse verify response:', e);
        throw new Error('Invalid response from server');
      }
      
      customLogger.log('Verify response status:', verifyRes.status);
      customLogger.log('Verify response data:', verifyResponseData);
      
      if (!verifyRes.ok) {
        throw new Error(verifyResponseData.message || `Verification failed with status ${verifyRes.status}`);
      }
      
      if (!verifyResponseData.token) {
        throw new Error('No session token received');
      }
      
      const sessionToken = verifyResponseData.token;
      customLogger.log('Session token received, length:', sessionToken.length);
      setWithExpiry('session_token', sessionToken);
      
      // Now fetch the bank accounts using the session token
      const accountsRes = await fetch(`${BASE_URL}/pub/bankaccount`, { 
        method: 'GET',
        headers: {
          ...commonHeaders,
          'Authorization': `Bearer ${sessionToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (!accountsRes.ok) {
        const errorData = await accountsRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch bank accounts');
      }
      
      const bankAccountsData = await accountsRes.json();
      customLogger.log('Fetched bank accounts:', bankAccountsData);
      
      // Update the state with the fetched bank accounts
      setBankAccounts(bankAccountsData);
      setWithExpiry('bankAccounts', bankAccountsData);
    } catch (err) {
      console.error('Fetch bank accounts error:', err);
    }
  };

  // Auto-fetch bank accounts on user change
  useEffect(() => { if (email) fetchUserBankAccounts(); }, [email]);

  // Load banks list from localStorage if available
  useEffect(() => {
    const stored = localStorage.getItem('banksList');
    if (stored) setBanksList(JSON.parse(stored));
  }, []);

  // Fetch list of banks and cache in localStorage
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await fetch(`${BASE_URL}/pub/bank`, {
        headers: commonHeaders
      });
        if (!res.ok) throw new Error('Failed to fetch banks list');
        const data = await res.json();
        customLogger.log('Fetched banks data:', data);
        let list = [];
        if (data.data && Array.isArray(data.data.banks)) {
          list = data.data.banks;
        } else if (Array.isArray(data.banks)) {
          list = data.banks;
        } else if (Array.isArray(data.data)) {
          list = data.data;
        } else if (Array.isArray(data)) {
          list = data;
        }
        setBanksList(list);
        localStorage.setItem('banksList', JSON.stringify(list));
      } catch (err) {
        console.error('Error fetching banks:', err);
      }
    };
    fetchBanks();
  }, []);

  // Resolve account name when 10-digit number entered
  useEffect(() => {
    if (newAccountNumber.length !== 10 || !newBankId) {
      setResolvedName('');
      setResolveError(null);
      return;
    }
    const resolveAccount = async () => {
      setIsResolving(true);
      setResolveError(null);
      try {
        const res = await fetch(
          `${BASE_URL}/pub/bankaccount/resolve?bankId=${newBankId}&accountNumber=${newAccountNumber}`
        );
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        setResolvedName(data.accountName || data.name);
      } catch (err) {
        console.error('Resolve error:', err);
        setResolveError(err.message);
      } finally {
        setIsResolving(false);
      }
    };
    resolveAccount();
  }, [newAccountNumber, newBankId]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    const amt = parseFloat(amount);
    
    // Validate amount
    if (isNaN(amt) || amt <= 0) {
      setError('Enter a valid amount');
      return;
    }
    
    // Check minimum withdrawal amount
    if (amt < MIN_WITHDRAWAL_AMOUNT) {
      setError(`Minimum withdrawal amount is ${MIN_WITHDRAWAL_AMOUNT} USDC`);
      return;
    }
    
    // Calculate fee and total amount (amount + fee)
    const fee = (amt * WITHDRAWAL_FEE_PERCENT) / 100;
    const totalAmount = amt + fee;
    
    // Check if user has enough balance including fee
    if (totalAmount > parseFloat(balances.USDC)) {
      setError(`Insufficient balance. You need ${totalAmount.toFixed(2)} USDC (${amt.toFixed(2)} + ${fee.toFixed(2)} fee)`);
      return;
    }
    
    if (!selectedAccount) {
      setError('Select a bank account');
      return;
    }

    setIsSubmitting(true);
    const transferId = `trx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let pajWallet = null;
    let sessionToken = getWithExpiry('session_token');

    try {
      // Log transfer initiation
      await logPajInteraction('transfer/initiate', {
        transferId,
        amount: amt,
        accountId: selectedAccount,
        timestamp: new Date().toISOString()
      });

      // Get the selected bank account details
      const selectedAccountDetails = bankAccounts.find(acc => acc.id === selectedAccount);
      if (!selectedAccountDetails) {
        throw new Error('Selected account not found');
      }

      // Use the Civic embedded wallet's publicKey (not address)
      const publicKey = civicWallet?.publicKey?.toBase58?.() || '';
      const timestamp = new Date().toISOString();
      const payload = { publicKey, accountId: selectedAccount, timestamp };
      // Check for existing wallet
      pajWallet = localStorage.getItem('pajWallet');
      if (pajWallet) {
        try {
          pajWallet = JSON.parse(pajWallet);
          // Log wallet check
          await logPajInteraction('wallet/check', {
            walletId: pajWallet.id,
            transferId
          });

          // Check if the wallet exists on the server
          const checkWalletRes = await fetch(`${BASE_URL}/pub/wallet/${pajWallet.id}`, {
            headers: {
              ...commonHeaders,
              ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {})
            }
          });

          if (checkWalletRes.ok) {
            // Wallet exists, use the response
            pajWallet = await checkWalletRes.json();
            localStorage.setItem('pajWallet', JSON.stringify(pajWallet));
            
            await logPajInteraction('wallet/check/success', {
              walletId: pajWallet.id,
              transferId
            });
          } else if (checkWalletRes.status === 404) {
            // Wallet doesn't exist, create a new one
            if (!civicWallet || typeof civicWallet.signMessage !== 'function') {
              throw new Error('Civic wallet does not support message signing. Please use a compatible wallet.');
            }
            
            await logPajInteraction('wallet/create/initiate', {
              publicKey,
              transferId
            });

            const message = JSON.stringify(payload);
            const signedMessage = await civicWallet.signMessage(
              new Uint8Array(Buffer.from(message, 'utf-8'))
            );
            const signature = bs58.encode(signedMessage);
            
            // Add wallet to PAJ
            const linkRes = await fetch(`${BASE_URL}/pub/wallet`, {
              method: 'POST',
              headers: {
                ...commonHeaders,
                ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {})
              },
              body: JSON.stringify({ payload, signature }),
            });
            if (!linkRes.ok) {
              const errorData = await linkRes.json().catch(() => ({}));
              throw new Error(errorData.message || 'Wallet link failed');
            }
            pajWallet = await linkRes.json();
            localStorage.setItem('pajWallet', JSON.stringify(pajWallet));
            
            await logPajInteraction('wallet/create/success', {
              walletId: pajWallet.id,
              transferId
            });
          } else {
            const errorData = await checkWalletRes.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to check wallet status');
          }
        } catch (err) {
          console.error('Wallet check error:', err);
          await logPajInteraction('wallet/error', {
            error: err.message,
            transferId
          }, null, err);
          throw new Error('Failed to verify wallet status');
        }
      } else {
        const error = new Error('No wallet found. Please ensure you have a connected wallet.');
        await logPajInteraction('wallet/not_found', {
          error: error.message,
          transferId
        }, null, error);
        throw error;
      }
      // --- USDC Transfer Logic ---
      // 1. Calculate fee and total amount
      const fee = (amt * WITHDRAWAL_FEE_PERCENT) / 100;
      const totalAmount = amt + fee;
      
      // 2. Get tx pool address
      const poolRes = await fetch(`${BASE_URL}/pub/txpool-address`, {
        headers: commonHeaders
      });
      if (!poolRes.ok) throw new Error('Failed to fetch tx pool address');
      const { address: poolAddress } = await poolRes.json();
      logger.log('TX Pool Address:', poolAddress);
      
      // 3. Log the fee and amount details
      logger.log('Withdrawal details:', {
        amount: amt,
        fee: fee,
        totalAmount: totalAmount,
        feePercentage: WITHDRAWAL_FEE_PERCENT,
        feeWallet: FEE_WALLET.toBase58()
      });
      // Log transfer creation
      await logPajInteraction('transfer/create', {
        transferId,
        amount: amt,
        from: publicKey,
        to: pajWallet.publicKey,
        accountId: selectedAccount
      });

      // Get the associated token accounts
      const fromTokenAccount = await getAssociatedTokenAddress(
        USDC_MINT,
        new PublicKey(publicKey)
      );
      
      // Get the fee wallet's associated token account
      const feeTokenAccount = await getAssociatedTokenAddress(
        USDC_MINT,
        FEE_WALLET,
        true // Allow owner off curve (for fee wallet)
      );

      // Get the recipient's token account
      const toTokenAccount = await getAssociatedTokenAddress(
        USDC_MINT,
        new PublicKey(pajWallet.publicKey)
      );

      // Get the latest blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      
      // Create a new transaction
      const tx = new Transaction();
      
      // Add the main transfer instruction (amount to recipient)
      tx.add(
        createTransferInstruction(
          fromTokenAccount,
          toTokenAccount,
          new PublicKey(publicKey),
          amt * Math.pow(10, 6), // USDC has 6 decimals
          [],
          TOKEN_PROGRAM_ID
        )
      );
      
      // Add the fee transfer instruction (fee to fee wallet)
      tx.add(
        createTransferInstruction(
          fromTokenAccount,
          feeTokenAccount,
          new PublicKey(publicKey),
          fee * Math.pow(10, 6), // USDC has 6 decimals
          [],
          TOKEN_PROGRAM_ID
        )
      );

      // Set the recent blockhash and fee payer
      tx.recentBlockhash = blockhash;
      
      // Sign the transaction
      const signed = await (
        civicWallet && typeof civicWallet.signTransaction === 'function'
          ? civicWallet.signTransaction(tx)
          : (() => { throw new Error('Civic wallet does not support transaction signing. Please use a compatible wallet.'); })()
      );
      
      // Log transaction signing
      await logPajInteraction('transaction/sign', {
        transferId,
        transaction: tx.serialize().toString('base64')
      });
      
      // Send the transaction
      const txid = await connection.sendRawTransaction(signed.serialize());
      
      // Log transaction submission
      await logPajInteraction('transaction/submit', {
        transferId,
        txid,
        amount: amt
      });
      
      // Wait for confirmation
      const confirmation = await connection.confirmTransaction(txid, 'confirmed');
      
      // Log confirmation
      await logPajInteraction('transaction/confirm', {
        transferId,
        txid,
        status: confirmation.value.err ? 'failed' : 'confirmed'
      }, confirmation);

      // Log successful transfer to our database
      try {
        await fetch('/api/bank/transfer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transferId,
            txid,
            amount: amt,
            fee: fee,
            feePercentage: WITHDRAWAL_FEE_PERCENT,
            feeWallet: FEE_WALLET.toBase58(),
            accountId: selectedAccount,
            recipientAccountNumber: pajWallet.publicKey,
            recipientBankCode: 'PAJ',
            recipientName: email || 'PAJ Wallet',
            providerReference: pajWallet.id, // PAJ wallet ID as provider reference
            status: 'completed',
            metadata: {
              paj_wallet_id: pajWallet.id,
              account_details: selectedAccountDetails,
              transaction_hash: txid,
              confirmed_at: new Date().toISOString(),
              solana_network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
            }
          })
        });
      } catch (dbError) {
        console.error('Failed to log transfer to database:', dbError);
        // Don't fail the operation if database logging fails
      }

      navigate('/dashboard', { 
        state: { 
          message: `USDC sent: ${amt}, tx: ${txid}`,
          transferId,
          txid
        } 
      });
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.message);
      
      // Log the error
      await logPajInteraction('transfer/error', {
        transferId,
        error: err.message,
        stack: err.stack
      }, null, err);
      
      // Log failed transfer to our database
      try {
        const logData = {
          transferId,
          amount: amt,
          fee: 0, // No fee for failed transfers
          accountId: selectedAccount,
          status: 'failed',
          failureReason: err.message,
          metadata: {
            error_details: {
              name: err.name,
              message: err.message,
              stack: err.stack,
              timestamp: new Date().toISOString()
            },
            // Include any available context
            transfer_attempt: {
              amount: amt,
              account_id: selectedAccount,
              paj_wallet_id: pajWallet?.id,
              timestamp: new Date().toISOString()
            }
          }
        };
        
        await fetch('/api/bank/transfer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(logData)
        });
      } catch (dbError) {
        console.error('Failed to log failed transfer to database:', dbError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Switch default bank account
  const switchBankAccount = async (accountId) => {
    if (!civicWallet || !civicWallet.publicKey) {
      setError('Wallet not connected');
      return;
    }
    
    try {
      const publicKey = civicWallet.publicKey.toBase58();
      const timestamp = new Date().toISOString();
      const payload = { publicKey, accountId, timestamp };
      
      // Sign the payload
      const message = JSON.stringify(payload);
      const signedMessage = await civicWallet.signMessage(new Uint8Array(Buffer.from(message, 'utf-8')));
      const signature = bs58.encode(signedMessage);
      
      // Get session token
      const sessionToken = getWithExpiry('session_token');
      
      // Call the switch bank account endpoint
      const res = await fetch(`${BASE_URL}/pub/wallet`, {
        method: 'PATCH',
        headers: {
          ...apiHeaders,
          ...(sessionToken && `${BASE_URL}/pub/initiate` === `${BASE_URL}/pub/initiate` ? { Authorization: `Bearer ${sessionToken}` } : {})
        },
        body: JSON.stringify({ payload, signature })
      });
      
      if (!res.ok) throw new Error('Failed to switch bank account');
      
      // Update the local state to reflect the change
      setBankAccounts(prevAccounts => 
        prevAccounts.map(account => ({
          ...account,
          isDefault: account.id === accountId
        }))
      );
      
      return true;
    } catch (err) {
      console.error('Switch bank account error:', err);
      setError(err.message || 'Failed to switch bank account');
      return false;
    }
  };

  // Add new bank account
  const handleAddAccount = async () => {
    setAddError(null);
    // Using Civic Auth email from hook
    if (!email) {
      setAddError('Cannot find user email');
      setIsAdding(false);
      return;
    }
    if (!newBankId || !newAccountNumber) {
      setAddError('Select bank and enter account number');
      return;
    }
    setIsAdding(true);
    try {
      // Initiate PAJ session
      await fetch(`${BASE_URL}/pub/initiate`, {
        method: 'POST',
        headers: initiateHeaders,
        body: JSON.stringify({ email }),
      });
      // Generate device signature
      const deviceSignature = await deviceFingerprint.get();
      
      // Verify session with OTP
      const verifyRes = await fetch(`${BASE_URL}/pub/verify`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ 
          email, 
          otp: '1234',
          deviceSignature 
        }),
      });
      if (!verifyRes.ok) {
        const errData = await verifyRes.json();
        throw new Error(errData.message || 'Verification failed');
      }
      const { token: sessionToken } = await verifyRes.json();
      setWithExpiry('session_token', sessionToken);
      // Add bank account using session token
      const res = await fetch(`${BASE_URL}/pub/bankAccount`, {
        method: 'POST',
        headers: {
          ...commonHeaders,
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          bankId: newBankId,
          accountNumber: newAccountNumber
        }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Add account failed');
      }
      const acct = await res.json();
      setBankAccounts(prev => {
        const updated = [...prev, acct];
        setWithExpiry('bankAccounts', updated);
        return updated;
      });
      setNewBankId('');
      setNewAccountNumber('');
      setShowAddDialog(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  // Mock data for UI demo
  const mockBanks = [
    { id: 'bank1', name: 'Bank of America', icon: '🏦' }
  ];
  const [selectedBank, setSelectedBank] = useState(mockBanks[0].id);
  const availableBalance = balances?.USDC || 0; // Use actual wallet balance

  return (
    <div className="sendbank-bg">
      <div className="sendbank-container">
        <h1 className="sendbank-title">Send to Bank</h1>

        {/* Country select dropdown */}
        <div className="sendbank-section">
          <div className="sendbank-section-label">Country</div>
          <select 
            className="sendbank-select"
            style={{
              width: '100%',
              padding: '1rem',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              fontSize: '1rem',
              marginTop: '0.5rem',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value="NG">Nigeria</option>
          </select>
        </div>

        {/* Add Bank Account Button - Improved */}
        <button 
          onClick={() => setShowAddDialog(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '1rem',
            margin: '1.5rem 0',
            borderRadius: '12px',
            border: '2px dashed #e0e0e0',
            backgroundColor: '#f8f9fa',
            color: '#2d6ae3',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': {
              borderColor: '#2d6ae3',
              backgroundColor: '#f0f5ff'
            }
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: '#2d6ae3',
            color: '#fff',
            fontSize: '1.2rem',
            lineHeight: '1',
            flexShrink: 0
          }}>+</span>
          <span>Add Bank Account</span>
        </button>

        {/* Select a bank account section */}
        <div className="sendbank-section" style={{marginTop:'2.5rem'}}>
          <div className="sendbank-section-label">Select a bank account</div>
          {bankAccounts.length > 0 ? (
            <select
              className="sendbank-select"
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                fontSize: '1rem',
                marginTop: '0.5rem',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select an account --</option>
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.bankName || 'Bank'} - {account.accountName || 'Account'} •••• {account.accountNumber?.slice(-4) || ''}
                </option>
              ))}
            </select>
          ) : (
            <div style={{
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '12px',
              textAlign: 'center',
              color: '#6c757d',
              marginTop: '0.5rem'
            }}>
              No bank accounts saved. Add one below.
            </div>
          )}
        </div>

        {/* Amount input */}
        <div className="sendbank-section" style={{marginTop:'2rem'}}>
          <div className="sendbank-section-label">Amount to send</div>
          <div style={{position: 'relative'}}>
            <span style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontWeight: '500'
            }}>$</span>
            <input
              className="sendbank-amount-input"
              type="number"
              placeholder={`Minimum ${MIN_WITHDRAWAL_AMOUNT} USDC`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              step="0.01"
              min={MIN_WITHDRAWAL_AMOUNT}
              style={{
                width: '100%',
                padding: '1rem 1rem 1rem 2.5rem',
                borderRadius: '12px',
                border: '2px solid #e0e0e0',
                fontSize: '1.2rem',
                fontWeight: '500',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2d6ae3'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              disabled={isSubmitting}
              required
            />
            <div className="text-xs text-gray-500 mt-2 ml-2">
              {WITHDRAWAL_FEE_PERCENT}% fee (min {MIN_WITHDRAWAL_AMOUNT} USDC)
              {amount && parseFloat(amount) > 0 && (
                <div className="mt-1">
                  Fee: {((parseFloat(amount) * WITHDRAWAL_FEE_PERCENT) / 100).toFixed(6)} USDC
                </div>
              )}
            </div>
            <div className="sendbank-balance" style={{
              textAlign: 'right',
              marginTop: '0.5rem',
              color: '#666',
              fontSize: '0.9rem'
            }}>
              Available: ${availableBalance.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
            </div>
          </div>
        </div>

        {/* Send button */}
        <button
          className="sendbank-sendbtn"
          type="button"
          onClick={() => {
            if (showRateDialog !== undefined) {
              setShowRateDialog(true);
            } else {
              handleSubmit();
            }
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>
        {/* Rate Confirmation Dialog */}
        {showRateDialog && (
          <RateConfirmDialog
            amount={amount}
            onClose={() => setShowRateDialog(false)}
            onConfirm={handleSubmit}
          />
        )}
      </div>

      {/* Add Bank Account Dialog */}
      {showAddDialog && (
        <div className="sendbank-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="sendbank-modal" style={{ background: '#fff', borderRadius: 16, padding: '2.2rem 1.7rem', minWidth: 320, maxWidth: '94vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: '1.3em', position: 'relative' }}>
            <div style={{fontWeight: 700, fontSize: 22, marginBottom: 10}}>Add Bank Account</div>
            <label style={{fontWeight: 500, marginBottom: 8, fontSize: 16}}>Bank
              <select
                value={newBankId}
                onChange={e => setNewBankId(e.target.value)}
                style={{ width: '100%', padding: '0.8em', borderRadius: 8, border: '1px solid #bdbdbd', marginTop: 8 }}
                required
              >
                <option value="">-- Select bank --</option>
                {banksList.map(bank => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </select>
            </label>
            <label style={{fontWeight: 500, marginBottom: 8, fontSize: 16}}>Account Number
              <input
                type="text"
                value={newAccountNumber}
                onChange={e => setNewAccountNumber(e.target.value.replace(/\D/g, '').slice(0,10))}
                style={{ width: '100%', padding: '0.8em', borderRadius: 8, border: newAccountNumber && newAccountNumber.length !== 10 ? '1.5px solid #ff4444' : '1.5px solid #bdbdbd', marginTop: 8, fontSize: 16, background: '#f9f9f9' }}
                maxLength={10}
                placeholder="Enter 10 digit account number"
                required
              />
            </label>
            {newAccountNumber && newAccountNumber.length !== 10 && (
              <div style={{color: '#ff4444', fontSize: '0.8rem', marginTop: 4}}>
                Account number must be exactly 10 digits
              </div>
            )}
            {newAccountNumber.length === 10 && (
              isResolving ? <div>Verifying account...</div>
                : resolvedName ? <div style={{color:'#16c784'}}>Account Name: {resolvedName}</div>
                : resolveError ? <div className="error" style={{color:'#ff4444'}}>{resolveError}</div>
                : null
            )}
            {addError && <div className="error" style={{marginBottom:8, color:'#ff4444'}}>{addError}</div>}
            <div style={{display:'flex',gap:12,justifyContent:'center'}}>
              <button
                type="button"
                className="action-btn"
                style={{minWidth:110,background:'#16c784',color:'#fff',borderRadius:10,fontWeight:600,padding:'0.8em 1.6em',fontSize:17,opacity: newAccountNumber.length === 10 && newBankId ? 1 : 0.6, cursor: newAccountNumber.length === 10 && newBankId ? 'pointer' : 'not-allowed'}}
                onClick={handleAddAccount}
                disabled={isAdding || newAccountNumber.length !== 10 || !newBankId}
              >
                {isAdding ? 'Saving...' : 'Save Account'}
              </button>
              <button type="button" className="action-btn" style={{minWidth:90,background:'#eee',color:'#222',borderRadius:10}} onClick={() => setShowAddDialog(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .sendbank-bg {
          min-height: 100vh;
          background: #f7f9fa;
        }
        .sendbank-container {
          max-width: 540px;
          margin: 56px auto 0 auto;
          padding: 0 1.2rem;
        }
        .sendbank-title {
          font-size: 2.15rem;
          font-weight: 800;
          color: #181c23;
          margin-bottom: 2.3rem;
        }
        .sendbank-select {
          width: 100%;
          padding: 1.05rem 1.2rem;
          border-radius: 14px;
          border: none;
          background: #f5f8fc;
          font-size: 1.09rem;
          color: #232a36;
          margin-bottom: 2.1rem;
          outline: none;
        }
        .sendbank-section-label {
          font-size: 1.09rem;
          font-weight: 700;
          color: #181c23;
          margin-bottom: 1.1rem;
        }
        .sendbank-addbank-row {
          display: flex;
          align-items: center;
          gap: 1.1rem;
        }
        .sendbank-addbank-btn {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          background: #f2f6fb;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: #232a36;
          cursor: pointer;
        }
        .sendbank-plus {
          font-size: 1.5rem;
          font-weight: 700;
        }
        .sendbank-addbank-label {
          font-size: 1.07rem;
          color: #232a36;
        }
        .sendbank-bankcard-row {
          display: flex;
          align-items: center;
          gap: 1.1rem;
          background: #fff;
          border-radius: 12px;
          padding: 1.1rem 1.2rem;
          margin-bottom: 1.5rem;
          box-shadow: none;
        }
        .sendbank-bankicon {
          font-size: 1.3rem;
        }
        .sendbank-bankname {
          font-size: 1.07rem;
          color: #232a36;
          font-weight: 500;
        }
        .sendbank-arrow {
          margin-left: auto;
          font-size: 1.2rem;
          color: #232a36;
          font-weight: 500;
        }
        .sendbank-amount-input {
          width: 100%;
          padding: 1.1rem 1.2rem;
          border-radius: 14px;
          border: none;
          background: #f5f8fc;
          font-size: 1.07rem;
          color: #232a36;
          margin-bottom: 0.5rem;
          margin-top: 0.5rem;
          outline: none;
          font-weight: 500;
          box-sizing: border-box;
        }
        .sendbank-amount-input::placeholder {
          color: #82a0c6;
          opacity: 1;
          font-weight: 400;
        }
        .sendbank-balance {
          color: #7e93b7;
          font-size: 0.97rem;
          margin-bottom: 1.9rem;
        }
        .sendbank-action-row {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 2.1rem;
        }
        .sendbank-action-btn {
          background: #e8f1fb;
          color: #232a36;
          font-weight: 700;
          font-size: 1.01rem;
          border: none;
          border-radius: 999px;
          padding: 0.7rem 2.1rem;
          cursor: pointer;
        }
        .sendbank-sendbtn {
          display: block;
          margin: 0 auto;
          background: #e8f1fb;
          color: #232a36;
          font-weight: 700;
          font-size: 1.05rem;
          border: none;
          border-radius: 999px;
          padding: 0.9rem 2.5rem;
          cursor: pointer;
        }
        @media (max-width: 600px) {
          .sendbank-container {
            margin-top: 24px;
            padding: 0 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}

export default SendBank;
